import {
  BillableElement,
  Board,
  boardHasBeenPaidFor,
  PartSelectionCheckState,
  PartSpecification,
} from "../../../../../reducers/uploadedBoard";
import * as styledElements from "../ElementsTableStyles";
import FlashPCBTooltip from "../../../../common/FlashPCBTooltip";
import { ElementPropertyKeyValue } from "../../../../../utils/properties";
import InfoIcon from "../../../../common/FlashPCBTooltip/InfoIcon";
import { useBoardFromContext } from "../../../../../utils/hooks/commonly-stored/uploaded-board/board";
import Icon from "../../../../common/Icon";
import { Part } from "../../../../../reducers/models/parts";
import { useAppSelector } from "../../../../../utils/hooks/store";
import { BLANK } from "../../../../../utils/hooks/commonly-stored/uploaded-board/billOfMaterials";

function shouldWarnAboutSpecifiedMPN(
  part_specification: PartSpecification | null,
  part: Part,
) {
  if (
    part_specification === null ||
    part_specification.mpn === null ||
    part_specification.mpn === ""
  ) {
    return false;
  }
  const specifiedMPN = part_specification.mpn.trim();
  return (
    specifiedMPN !== part.name &&
    !part.alternate_names.includes(part_specification.mpn)
  );
}

function shouldWarnAboutSpecifiedManufacturer(
  part_specification: PartSpecification | null,
  part: Part,
) {
  if (
    part_specification === null ||
    part_specification.manufacturer === null ||
    part_specification.manufacturer === ""
  ) {
    return false;
  }
  return part.manufacturer !== part.manufacturer;
}

function IconWithTooltipWarning({
  warning,
  iconPath,
}: {
  warning: string;
  iconPath: string;
}) {
  return (
    <FlashPCBTooltip content={warning}>
      <Icon src={iconPath} alt="alert" />
    </FlashPCBTooltip>
  );
}

function PartWarning({ element }: { element: BillableElement }) {
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );

  if (isAdminOrEngineer) {
    if (
      element.part_selection_check.state == PartSelectionCheckState.UNCHECKED
    ) {
      return (
        <IconWithTooltipWarning
          warning={
            "Part selection unchecked. Run part selection checks from additional tools dropdown (it could take a minute)."
          }
          iconPath={"/icons/alert_red.svg"}
        />
      );
    }

    const partSelectionCheckMessage = element.part_selection_check.message;
    if (partSelectionCheckMessage) {
      return (
        <IconWithTooltipWarning
          warning={partSelectionCheckMessage}
          iconPath={"/icons/alert_red.svg"}
        />
      );
    }
  }
}

function PartSpecificationWarning({
  element,
  part,
}: {
  element: BillableElement;
  part: Part;
}) {
  if (shouldWarnAboutSpecifiedMPN(element.part_specification, part)) {
    return (
      <IconWithTooltipWarning
        warning={`The part selected does not match what was originally specified: ${element.part_specification?.mpn}`}
        iconPath={"/icons/alert_yellow.svg"}
      />
    );
  }
  if (shouldWarnAboutSpecifiedManufacturer(element.part_specification, part)) {
    return (
      <IconWithTooltipWarning
        warning={`The manufacturer of the part selected does not match what was originally specified: ${element.part_specification?.manufacturer}`}
        iconPath={"/icons/alert_yellow.svg"}
      />
    );
  }
}

export default function PartSelection({
  element,
  part,
}: {
  element: BillableElement;
  part: Part | null;
}) {
  const board = useBoardFromContext().board as Board;

  const partText = () => {
    if (boardHasBeenPaidFor(board)) {
      return element.do_not_populate
        ? BLANK
        : "This part is no longer available.";
    } else {
      return BLANK;
    }
  };

  if (part === null) {
    return partText();
  } else {
    return (
      <styledElements.HasInfoIcon>
        {part.name}
        {part.properties.length > 0 && (
          <FlashPCBTooltip
            content={
              <div style={{ textAlign: "left" }}>
                {part.properties.map((property) => (
                  <ElementPropertyKeyValue
                    key={`${property.raw} ${property.parameter.id}`}
                    property={property}
                  />
                ))}
              </div>
            }
          >
            <InfoIcon />
          </FlashPCBTooltip>
        )}
        <PartWarning element={element} />
        <PartSpecificationWarning element={element} part={part} />
      </styledElements.HasInfoIcon>
    );
  }
}
